import React, { useState } from 'react';
import ITask from 'src/ServerEntities/ITask';
import { SessionContext } from 'src/Views/SessionContext';
import styled from 'styled-components';
import { getRhapsodyHeader, getTasks } from '../ramService';
import dateConverter from 'src/UsefulFunctions/dateConverter';
import ErrorBox from 'src/SharedComponents/ErrorBox';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import IRhapsodyServer from 'src/ServerEntities/IRhapsodyServer';
import { ContentContainer } from './IntersystemsDashboard';
import ServerSelector from 'src/SharedComponents/Select/ServerSelector';
import useCookie from 'src/CustomHooks/useCookie';


const TasksContainer = styled.div`
  width: 100%;
  max-width: 600px;
  border-radius: 15px;
  overflow: hidden;
  border: 4px solid #3F9FD5;
  padding: 4px;
  margin: 24px 15px;
`;

const Header = styled.div`
  background-color: #1a85b8;
  color: white;
  padding: 10px;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
`;

const SubHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 1fr 1fr 1fr;
  padding: 8px 0;
  text-align: center;
`;

const HeaderText = styled.div`
  color: #1a85b8;
  font-weight: bold;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: '▼';
    margin-left: 4px;
    font-size: 10px;
  }
`;

const TaskRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 1fr 1fr 1fr;
  padding: 8px 0;
  text-align: center;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

interface StatusBadgeProps {
  statusType: string;
}

const StatusBadge = styled.div<StatusBadgeProps>`
  width: 38px;
  height: 25px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  margin: 0 auto;
  background-color: ${props => {
    switch(props.statusType) {
      case 'E': return '#ff4545';
      case 'W': return '#ff9a45';
      case 'Q': return '#45ffb4';
      case 'D': return '#c0c0c0';
      case 'R': return '#7dff45';
      default: return '#c0c0c0';
    }
  }};
`;

interface TaskNameProps {
  status: string;
}

const TaskName = styled.div<TaskNameProps>`
  text-align: left;
  padding-left: 10px;
  font-weight: bold;
  color: ${props => props.status === 'E' ? '#ff4545' : 'inherit'};
`;

const DateText = styled.div`
  font-size: 12px;
  color: #555;
  font-weight: bold;
`;

const Tasks = () => { 
  const { state, dispatch } = React.useContext(SessionContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tasks, setTasks] = useState<ITask[]>([]);
  const [data, setData] = React.useState(undefined as unknown as IRhapsodyServer[]);
  const [selectedNamespace, setSelectedNamespace] = useCookie<string>('selectedNamespace', '');
  const headerInfo = state.headerInfo ? state.headerInfo : data;

  const selectedServer = state.selectedSever ?? 0;

  const setSelectedServer = (index: number) => {
      dispatch({ type: "setSelectedServer", payload: index });
  };
 
  React.useEffect(() => {
      setLoading(true);
      getRhapsodyHeader(state.webToken, (serverData: IRhapsodyServer[]) => {
          if (serverData[selectedServer]) {
              getTasks(state.webToken, serverData[selectedServer].id, (data: ITask[]) => {
                  setTasks(data);
                  setError("");
                  setLoading(false);
              }, (errorMessage: string) => {
                  setError(errorMessage);
                  setLoading(false);
              });
          } else {
              setLoading(false);
          }
          setData(serverData);
          dispatch({ type: "setHeaderInfo", payload: serverData });
      }, (errorMessage: string) => {
          setLoading(false);
          setError(errorMessage);
      }, selectedNamespace);
  }, [selectedNamespace, selectedServer]);

  return <ContentContainer>
    <ServerSelector headerInfo={headerInfo} selectedNamespace={selectedNamespace}
          selectedServer={selectedServer} setSelectedNamespace={setSelectedNamespace} setSelectedServer={setSelectedServer} />
    <TasksContainer>
      <LoadingIndicator type="Linear" show={loading} />
      {error && <ErrorBox>{error}</ErrorBox>}
      <Header>Tasks</Header>
      <SubHeader>
        <div></div>
        <div></div>
        <HeaderText>Start</HeaderText>
        <HeaderText>Finish</HeaderText>
        <HeaderText>Next</HeaderText>
      </SubHeader>
      {tasks.map((task, index) => (
        <TaskRow key={index}>
          <div>
            <StatusBadge statusType={task.status}>{task.status}</StatusBadge>
          </div>
          <TaskName status={task.status}>{task.taskName}</TaskName>
          <DateText>{dateConverter(task.startDate, true)}</DateText>
          <DateText>{dateConverter(task.finishDate, true)}</DateText>
          <DateText>{dateConverter(task.nextDate, true)}</DateText>
        </TaskRow>
      ))}
    </TasksContainer>
  </ContentContainer>;
};

export default Tasks;

